// ABOUTME: Runs the notificationCreated subscription and keeps the Apollo cache live.
// ABOUTME: Prepends new notifications and bumps the unread count with no page refresh.

import { useApolloClient } from '@apollo/client';
import { useAuth } from '@src/hooks';
import { ReactNode } from 'react';

import {
    NotificationsDocument,
    NotificationsQuery,
    UnreadNotificationCountDocument,
    UnreadNotificationCountQuery,
    useNotificationCreatedSubscription,
} from '../graphql/notifications.generated';

const NotificationSubscriptionContextProvider = ({ children }: { children: ReactNode }) => {
    const { sessionData } = useAuth();
    const client = useApolloClient();

    const company = sessionData?.attributes?.company ?? '';
    const userId = sessionData?.username ?? '';
    // dealerships are not declared on the session type but may be present at runtime; read them
    // defensively so dealership-scoped users still receive their dealership notifications live.
    const dealerships = (sessionData?.attributes as { dealerships?: string[] } | undefined)
        ?.dealerships;
    const roles = sessionData?.roles;

    useNotificationCreatedSubscription({
        shouldResubscribe: true,
        skip: !company || !userId,
        variables: { company, userId, dealerships, roles },
        onData: ({ data }) => {
            const notification = data.data?.notificationCreated;
            if (!notification) return;

            // A fresh notification is unread, so it belongs in both list variants
            // (unread-only and "Show all"); update whichever entries are in the cache.
            [true, false].forEach(onlyUnread => {
                client.cache.updateQuery<NotificationsQuery>(
                    { query: NotificationsDocument, variables: { onlyUnread } },
                    existing => {
                        if (!existing) return existing;
                        if (existing.notifications.some(n => n.id === notification.id)) {
                            return existing;
                        }
                        return { notifications: [notification, ...existing.notifications] };
                    },
                );
            });

            client.cache.updateQuery<UnreadNotificationCountQuery>(
                { query: UnreadNotificationCountDocument },
                existing =>
                    existing
                        ? { unreadNotificationCount: existing.unreadNotificationCount + 1 }
                        : existing,
            );
        },
    });

    // This provider only drives the live subscription; it renders its children unchanged.
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
};

export default NotificationSubscriptionContextProvider;
